import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { collection, query, where, getDocs, doc, updateDoc, getDoc } from "firebase/firestore";

function StaffDashboard({ user, onNavigate }) {
  const [activeTab, setActiveTab] = useState('schedule');
  
  const handleShareToClients = async () => {
    const shareText = `Book your next haircut with me on BookMyStyle! Use my shop code: ${user.shopId}`;
    const shareData = {
      title: 'Book with me on BookMyStyle',
      text: shareText,
      url: window.location.origin
    };
    
    if (navigator.share) {
      try { await navigator.share(shareData); } catch (err) { console.log('Share cancelled', err); }
    } else {
      navigator.clipboard.writeText(`${shareText} | Link: ${window.location.origin}`);
      alert("Invite code and link copied to clipboard! You can now paste it in Instagram or WhatsApp.");
    }
  };
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [barberStatus, setBarberStatus] = useState('');

  const [shopData, setShopData] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]); 
  const [scheduleSlots, setScheduleSlots] = useState([]);
  const [barberServices, setBarberServices] = useState([]);

  // Modal States
  const [isFillModalOpen, setIsFillModalOpen] = useState(false);
  const [fillTime, setFillTime] = useState('');
  
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [newService, setNewService] = useState({ name: '', price: '', duration: '' });
  const [editingIndex, setEditingIndex] = useState(null);

  
  useEffect(() => {
    const fetchShopAndServices = async () => {
      if (!user?.shopId) return;
      const shopSnap = await getDoc(doc(db, "shops", user.shopId));
      if (shopSnap.exists()) {
        const shop = shopSnap.data();
        setShopData(shop);
        const me = shop.barbers?.find(b => b.name === user.name);
        if (me) setBarberServices(me.services || []);
      }
    };
    fetchShopAndServices();
  }, [user]);

  useEffect(() => {
    const fetchDayData = async () => {
      if (!shopData || !selectedDate) return;

      const qApps = query(collection(db, "appointments"), 
        where("shopId", "==", user.shopId), where("barberName", "==", user.name), where("date", "==", selectedDate)
      );
      const appSnap = await getDocs(qApps);
      setAppointments(appSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const qBlocked = query(collection(db, "blocked_slots"),
        where("shopId", "==", user.shopId), where("barberName", "==", user.name), where("date", "==", selectedDate)
      );
      const blockSnap = await getDocs(qBlocked);
      setBlockedSlots(blockSnap.docs.map(d => d.data().time));

      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const todayHours = shopData.schedule[days[new Date(selectedDate).getDay()]];

      if (!todayHours || todayHours.closed) { setScheduleSlots([]); return; }

      const slots = [];
      let currentTime = todayHours.open;
      while (currentTime < todayHours.close) {
        slots.push(currentTime);
        let [hours, mins] = currentTime.split(':').map(Number);
        mins += 30; if (mins >= 60) { hours += 1; mins -= 60; }
        currentTime = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
      }
      setScheduleSlots(slots);
    };
    fetchDayData();
  }, [shopData, selectedDate, user]);

  const compressImage = (file, maxWidth, maxHeight, quality) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        // Returns a compressed JPEG instead of a massive PNG/original format
        resolve(canvas.toDataURL('image/jpeg', quality)); 
      };
    };
  });
};

  const saveServicesToDb = async (updatedServices) => {
    const updatedBarbers = shopData.barbers.map(b => b.name === user.name ? { ...b, services: updatedServices } : b);
    await updateDoc(doc(db, "shops", user.shopId), { barbers: updatedBarbers });
    setShopData({...shopData, barbers: updatedBarbers});
    setBarberServices(updatedServices);
  };

  const handleSaveService = async (e) => {
    e.preventDefault();
    let updatedServices = [...barberServices];
    if (editingIndex !== null) updatedServices[editingIndex] = newService; 
    else updatedServices.push(newService); 
    await saveServicesToDb(updatedServices);
    setIsServiceModalOpen(false);
    setNewService({ name: '', price: '', duration: '' });
    setEditingIndex(null);
  };

  const openEditModal = (svc, idx) => {
    setNewService(svc);
    setEditingIndex(idx);
    setIsServiceModalOpen(true);
  };

  const handleDeleteService = async (serviceName) => {
    if(window.confirm(`Delete ${serviceName}?`)){
      await saveServicesToDb(barberServices.filter(s => s.name !== serviceName));
    }
  };

  // --- NEW: PORTFOLIO UPLOAD FOR BARBER ---
  // Replace your existing handlePortfolioUpload with this:
  const handlePortfolioUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Compress the image to max 800x800px at 70% quality
      const compressedBase64 = await compressImage(file, 800, 800, 0.7);
      
      const me = shopData.barbers.find(b => b.name === user.name);
      const currentPortfolio = me.portfolio || [];
      const newPortfolio = [...currentPortfolio, compressedBase64].slice(0, 15); // Max 15 photos

      const updatedBarbers = shopData.barbers.map(b => b.name === user.name ? { ...b, portfolio: newPortfolio } : b);
      await updateDoc(doc(db, "shops", user.shopId), { barbers: updatedBarbers });
      setShopData({...shopData, barbers: updatedBarbers});
      alert("Portfolio photo added!");
    }
  };

  const handleDeletePortfolioPhoto = async (indexToRemove) => {
    if(window.confirm("Delete this photo from your lookbook?")) {
      const me = shopData.barbers.find(b => b.name === user.name);
      const newPortfolio = me.portfolio.filter((_, i) => i !== indexToRemove);
      const updatedBarbers = shopData.barbers.map(b => b.name === user.name ? { ...b, portfolio: newPortfolio } : b);
      await updateDoc(doc(db, "shops", user.shopId), { barbers: updatedBarbers });
      setShopData({...shopData, barbers: updatedBarbers});
    }
  };

  const myProfile = shopData?.barbers?.find(b => b.name === user.name);

  return (
    <div className="dashboard-container">
      <aside className="dash-sidebar staff-sidebar">
        <div className="dash-logo" onClick={() => onNavigate('home')} style={{ cursor: 'pointer', transition: '0.2s' }} title="Back to Home Page">
          💈 Back to Home
        </div>
        <nav>
          <button className={activeTab === 'schedule' ? 'active' : ''} onClick={() => setActiveTab('schedule')}>📅 Daily Schedule</button>
          <button className={activeTab === 'services' ? 'active' : ''} onClick={() => setActiveTab('services')}>📝 Services & Portfolio</button>
          <button onClick={() => auth.signOut()} style={{marginTop: 'auto', color: '#ef4444'}}>🚪 Logout</button>
        </nav>
      </aside>

      <main className="dash-content">
        <header className="dash-header">
          <div>
            <h1>{user.name}'s Dashboard</h1>
            <p>Manage your daily schedule and appointments.</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="outline-btn" onClick={handleShareToClients} style={{ background: '#ec4899', color: 'white', border: 'none', padding: '10px 15px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
              📲 Share to Clients
            </button>
            <button className="premium-book-btn" onClick={() => onNavigate('home')}>View Live Page</button>
          </div>
        </header>

        {activeTab === 'schedule' && (
          <div className="admin-section">
            <h2>Agenda</h2>
            <div className="schedule-timeline">
              {scheduleSlots.map(time => {
                const bookedApp = appointments.find(a => a.time === time);
                if (blockedSlots.includes(time)) return null; 
                if (bookedApp) return (
                  <div key={time} className="client-row booked">
                    <span className="time">{time}</span>
                    <div className="client-details"><span className="client-name">{bookedApp.clientName}</span><span className="service">{bookedApp.serviceName}</span></div>
                    <span className="status active">Confirmed</span>
                  </div>
                );
                return (
                  <div key={time} className="client-row free-slot">
                    <span className="time">{time}</span><span className="service empty">Available Slot</span>
                    <button className="premium-login-btn small" onClick={() => { setFillTime(time); setIsFillModalOpen(true); }}>+ Fill</button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'services' && (
          <div className="admin-section">
            
            {/* --- NEW: PORTFOLIO SECTION --- */}
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
              {/* --- NEW: BARBER STATUS / STORY UPDATE --- */}
            <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0', marginBottom: '30px' }}>
              <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>💭 Current Status / Story</h3>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input 
                  type="text" 
                  placeholder="e.g., 'Had a cancellation, taking walk-ins at 3PM!'" 
                  value={barberStatus}
                  onChange={(e) => setBarberStatus(e.target.value)}
                  style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid #cbd5e1' }}
                />
                <button 
                  className="premium-login-btn"
                  onClick={async () => {
                    const updatedBarbers = shopData.barbers.map(b => b.name === user.name ? { ...b, story: barberStatus } : b);
                    await updateDoc(doc(db, "shops", user.shopId), { barbers: updatedBarbers });
                    setShopData({...shopData, barbers: updatedBarbers});
                    alert("Status updated!");
                  }}
                >
                  Post Status
                </button>
              </div>
            </div>
              <h2>My Lookbook Gallery</h2>
              <label className="premium-login-btn" style={{cursor: 'pointer', display: 'inline-block'}}>
                + Upload Haircut Photo
                <input type="file" accept="image/*" style={{display: 'none'}} onChange={handlePortfolioUpload} />
              </label>
            </div>

            {myProfile?.portfolio && myProfile.portfolio.length > 0 ? (
              <div style={{ display: 'flex', gap: '15px', overflowX: 'auto', paddingBottom: '10px', marginBottom: '40px' }}>
                {myProfile.portfolio.map((img, i) => (
                  <div key={i} style={{position: 'relative', flexShrink: 0}}>
                    <img src={img} alt="Portfolio" style={{ width: '120px', height: '120px', borderRadius: '16px', objectFit: 'cover', border: '1px solid #e2e8f0' }} />
                    <button onClick={() => handleDeletePortfolioPhoto(i)} style={{position: 'absolute', top: '-5px', right: '-5px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>✕</button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="empty-text" style={{marginBottom: '40px'}}>You haven't uploaded any photos of your cuts yet.</p>
            )}

            <hr style={{ border: 0, borderTop: '2px dashed #f1f5f9', margin: '30px 0' }}/>

            {/* --- SERVICES SECTION --- */}
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
              <h2>Manage My Services</h2>
              <button className="premium-login-btn" onClick={() => { setNewService({ name: '', price: '', duration: '' }); setEditingIndex(null); setIsServiceModalOpen(true); }}>+ Add Service</button>
            </div>
            
            {barberServices.length === 0 && <p className="empty-text">You haven't added any services yet.</p>}
            
            {barberServices.map((svc, idx) => (
              <div key={idx} className="admin-service-item" style={{display: 'flex', alignItems: 'center'}}>
                <div style={{flex: 1}}>
                  <strong style={{fontSize: '16px', display: 'block'}}>{svc.name}</strong>
                  <span style={{color: '#64748b', fontSize: '13px'}}>{svc.duration}</span>
                </div>
                <span className="price-tag" style={{marginRight: '20px', fontSize: '16px'}}>{svc.price}</span>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button className="text-btn" style={{ color: '#3b82f6' }} onClick={() => openEditModal(svc, idx)}>Edit</button>
                  <button className="text-btn danger" onClick={() => handleDeleteService(svc.name)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* SERVICE MODAL */}
      {isServiceModalOpen && (
        <div className="login-modal-overlay" onClick={() => setIsServiceModalOpen(false)}>
          <div className="login-modal-content" onClick={e => e.stopPropagation()} style={{maxWidth: '400px'}}>
            <button className="modal-close-btn" onClick={() => setIsServiceModalOpen(false)}>✕</button>
            <h2>{editingIndex !== null ? 'Edit Service' : 'Add New Service'}</h2>
            <form className="auth-form" onSubmit={handleSaveService}>
              <div className="input-group"><label>Service Name</label><input type="text" value={newService.name} onChange={e => setNewService({...newService, name: e.target.value})} required/></div>
              <div className="input-group"><label>Price</label><input type="text" value={newService.price} onChange={e => setNewService({...newService, price: e.target.value})} required/></div>
              <div className="input-group"><label>Duration</label><input type="text" placeholder="e.g. 30m or 1h" value={newService.duration} onChange={e => setNewService({...newService, duration: e.target.value})} required/></div>
              <button type="submit" className="submit-auth-btn staff">{editingIndex !== null ? 'Update Service' : 'Save Service'}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default StaffDashboard;